/**
 * versioneInLinea.js — la «terza difesa» (Alex, 2 ottobre 2026): una pagina
 * aperta si accorge che e' uscita una versione nuova del gestionale e si
 * ricarica da sola, ma solo quando nessuno sta scrivendo.
 *
 * IL DIFETTO. Lo scambio del rilascio (scripts/rilascia.js) mette in linea
 * il bundle nuovo in un istante, ma le pagine gia' aperte restano sul
 * vecchio finche' qualcuno non le ricarica: la mattina del 2/10 i tablet
 * sono rimasti sul bundle di prima fino alle 7:29, quando hanno ricaricato
 * insieme quello nuovo. Un frontend vecchio
 * che parla con un backend nuovo e' un difetto che nessuna prova vede,
 * e per questo i rilasci che cambiano il frontend si facevano di sera.
 *
 * LA VERSIONE E' UNA COPPIA: gli indirizzi di main.<impronta>.js e
 * main.<impronta>.css. Quella PROPRIA si legge dal documento al caricamento;
 * quella IN LINEA da /index.html, chiesto senza cache, con la stessa
 * espressione che usa rilascia.js (bundle()) piu' quella del css: una
 * modifica di solo stile cambia solo il css. Conta «diversa», non «piu'
 * nuova»: anche il ritorno indietro a build.prec e' una versione da
 * prendere. Senza main.<impronta>.js nel documento (npm start, il jsdom di
 * rotte:runtime) il controllo resta spento.
 *
 * PRIMA DI RICARICARE i file nuovi devono esserci DAVVERO: una HEAD senza
 * cache su main.<nuovo>.js (e .css) che risponda 200 col tipo giusto. Il
 * tipo conta quanto il 200: Nginx ha `try_files ... /index.html`, quindi un
 * file che non c'e' risponde 200 con l'HTML dell'applicazione. A scambio a
 * meta' o a server giu' non si ricarica: sul tablet vorrebbe dire una
 * pagina bianca davanti al paziente.
 *
 * SI RICARICA SOLO SE TUTTO E' VERO, ricontrollato un istante prima:
 *   a. nessun modulo sporco (uscitaProtetta: i moduli col «Salva» e i campi
 *      rossi che non sono passati);
 *   b. nessun campo che si salva da solo scritto e non ancora uscito
 *      (uscitaSicura);
 *   c. nessuna richiesta in volo (il contatore degli intercettori di
 *      services/api.js): un salvataggio a meta', un caricamento di file.
 *      Se a fermare la ricarica sono SOLO letture, si riprova appena
 *      tornano (services/api.js, allaQuiete), non al timer dopo: le letture
 *      periodiche degli avvisi del Layout partono insieme al controllo, e
 *      aspettare il timer voleva dire trovarle di nuovo in volo, per sempre
 *      (vedi alRitornoDelleLetture);
 *   d. nessun campo «trattenuto»: e' la rete per i moduli che non
 *      dichiarano niente (l'incasso della cassa, lo storno). Si ricorda
 *      ogni campo toccato fuori da [data-salva-da-solo], e ogni tela di
 *      firma toccata; conta finche' e' nel documento, OPPURE finche' e'
 *      aperta la finestra che lo conteneva ([role="dialog"] o
 *      [aria-modal="true"]): in una finestra a passi il passo smontato vive
 *      ancora nello stato del componente (la scelta e la firma del passo 1
 *      di CompletamentoConsensi, al passo 2). Chiusa la finestra, il campo
 *      se ne va con lei. Le caselle di ricerca e i filtri non trattengono
 *      (type=search, o l'attributo data-non-trattiene). L'elenco si pota
 *      a ogni controllo e quando il tablet torna all'attesa, non solo
 *      quando si sta per ricaricare: tiene gli ELEMENTI, cioe' i valori
 *      scritti e i pixel delle firme (vedi campiTrattenuti);
 *   e. nessun gesto negli ultimi 5 secondi e nessun dito o tasto del mouse
 *      ancora premuto (la pressione lunga di «Timbra», chi sta toccando);
 *   f. sul tablet NO, salvo che la pagina abbia dichiarato l'attesa
 *      (useRicaricaPermessa in pages/Tablet.jsx): nessun'altra schermata del
 *      tablet si ricarica mai;
 *   g. nessuna schermata che mostra un segreto UNA volta sola - la chiave di
 *      un tablet, i codici di recupero, il PIN generato - finche' e' a video
 *      (useTrattieniVersione). Li' niente e' sporco ne' trattenuto: il modulo
 *      che l'ha generato e' gia' chiuso, e chi lo legge sta andando a
 *      scriverlo altrove, lasciando la pagina ferma;
 *   h. nessun errore a video (notificaErrore, che resta finche' qualcuno non
 *      lo chiude): la ricarica lo cancellerebbe, e chi torna alla scheda
 *      troverebbe una pagina pulita al posto di «Non riesco a caricare il
 *      file.», cioe' un fallimento diventato silenzioso;
 *   i. la rete (navigator.onLine non e' false): ricaricata senza rete, la
 *      pagina resta sulla pagina d'errore del browser - a pagina nascosta
 *      fino al ritorno di qualcuno.
 *
 * I MOMENTI: (a) al cambio di percorso del router - la ricarica porta alla
 * destinazione, e chi ha cliccato una voce di menu non se ne accorge; solo
 * con la rete (navigator.onLine) e con i file nuovi verificati da meno di 15
 * secondi, altrimenti la HEAD si rifa' prima di decidere: un clic sul menu
 * col Wi-Fi caduto non deve portare alla pagina d'errore del browser; (b) a
 * pagina nascosta da almeno 5 secondi, ANCHE SE ERA GIA' NASCOSTA QUANDO E'
 * USCITA LA VERSIONE (Alex, 2/10 pomeriggio: «Scheda gia' nascosta al
 * rilascio: si ricarica anche da nascosta, mai con un modulo sporco»; mai
 * sulle pagine che lasciano traccia, vedi sotto), e la
 * pagina resta nascosta: chi torna alla scheda trova la versione nuova, sulla
 * schermata dell'indirizzo; (c) a pagina visibile e ferma da 2 minuti
 * (nessun gesto, nessuno scorrimento, nessun movimento del mouse); (d) sul
 * tablet in attesa, al controllo successivo. Il controllo gira ogni 60
 * secondi, a pagina visibile e a pagina nascosta, e subito al ritorno
 * visibile e all'evento `online`. A pagina nascosta pero' si va in rete
 * solo se si potrebbe ricaricare davvero (ostacolo: tutte le guardie qui
 * sopra tranne la versione) e non piu' di una volta al giro: una scheda in
 * secondo piano con un modulo a meta' non chiede niente, per ore, e appena
 * il modulo e' pulito controlla al giro dopo. E a pagina nascosta si
 * ricarica solo su una verifica dei file nuovi di meno di 15 secondi,
 * riuscita nell'ultimo controllo. Una pagina che nasce nascosta (aperta in
 * secondo piano, o ricaricata da qui) comincia subito il suo giro. Nei
 * momenti (b), (c) e (d), fermata dalle sole letture in volo, la pagina
 * riprova quando tornano, con le guardie da capo e su una verifica ancora
 * fresca (alRitornoDelleLetture).
 *
 * LE PAGINE CHE LASCIANO TRACCIA (Alex, 2/10 sera, «strada C»). Una ricarica
 * rifa' le letture della pagina, e quelle che passano da traccia()
 * (backend/middleware/auditAccessi.js) scrivono nel registro degli accessi
 * a nome di chi aveva la pagina aperta: misurato dal revisore, 7 righe
 * d'apertura su una cartella aperta da mezz'ora, che nessuno aveva fatto.
 * Sulle pagine di PAGINE_CON_TRACCIA la ricarica automatica avviene SOLO al
 * cambio di schermata fatto dalla persona, il momento (a): mai da ferma, mai
 * da nascosta, e da nascosta la pagina non va nemmeno in rete, perche' non
 * potrebbe ricaricare (ostacolo: 'traccia'). L'avviso dei 30 minuti li' dice
 * «quando cambierai schermata». Il resto del gestionale si aggiorna come
 * prima; il tablet non cambia. L'elenco non e' scritto a memoria: e' quello
 * che le pagine chiedono all'apertura, e lo scenario 15 (caso 17) apre ogni
 * rotta (dalla tabella navigazione/schermate.js), attribuisce ogni lettura
 * alla rotta del backend come la sceglierebbe Express
 * (scripts/tracceDelBackend.js) e fallisce se una
 * pagina che lascia traccia non e' nell'elenco, o se una dell'elenco non ne
 * lascia.
 *
 * I TIMER A PAGINA NASCOSTA (misurati il 2/10 su Chromium 131, in una
 * scheda davvero in secondo piano, CDP senza le opzioni di Playwright che
 * tengono le pagine visibili; nessun dato). Su una pagina vuota: nei primi
 * 5 minuti un setInterval da 1 s scatta ogni 1000 ms, la catena da 5 s di
 * questo file ogni 5000-6000 ms, un setInterval da 60 s ogni 60000; dopo 5
 * minuti tutti e tre UNA VOLTA AL MINUTO, allineati. Su /dashboard e sulla
 * scheda di un paziente, con la sessione e gli avvisi del Layout (revisione
 * della sera: la prima sonda era su /login, senza sessione ne' avvisi, e
 * non poteva vedere il difetto delle letture): il primo controllo da
 * nascosta dopo 5 s, il secondo dopo altri 63-109 s, poi uno ogni 60 s;
 * fino ai 5 minuti le letture degli avvisi cadono a 30 s dai controlli,
 * dopo NELLO STESSO RISVEGLIO, prima o dopo il controllo (vedi
 * alRitornoDelleLetture). Quindi la pagina nascosta va in rete al piu' una
 * volta al minuto anche dopo ore, e una scheda gia' nascosta prende la
 * versione nuova al primo controllo dopo il rilascio: misurato col bundle
 * vero e i tempi veri, 25-43 s dopo, vedi docs/prove-percorsi.md. Firefox,
 * Safari e le schede che un browser
 * congela o scarica non sono misurati: una scheda congelata non gira, e si
 * aggiorna al ritorno visibile, come prima.
 *
 * IL FRENO contro il giro infinito (una cache in mezzo, un ritorno indietro
 * durante la ricarica): in sessionStorage si scrivono solo l'impronta di
 * destinazione e l'ora. Se dopo la ricarica la versione propria non e'
 * quella, verso la stessa impronta non si riprova per 30 minuti. Senza
 * sessionStorage, una pagina nata da una ricarica non ne fa un'altra da
 * sola.
 *
 * A SCHERMO NIENTE, salvo un caso: sul PC, se la versione nuova aspetta da
 * piu' di 30 minuti, un avviso solo (MSG_VERSIONE_NUOVA), e solo a pagina
 * visibile: in una scheda nascosta sparirebbe prima che qualcuno la guardi.
 * Sul tablet mai.
 * Se il controllo non riesce (rete, 404, 502, HTML senza impronta, HEAD
 * diversa da 200) non succede niente e si riprova al giro dopo: niente
 * messaggi e niente console.error, che verificaRotte boccia.
 *
 * LA «SECONDA DIFESA» (ricaricare su un errore di caricamento di un pezzo a
 * richiesta) c'e' dal 2/10 notte, solo per il Referto, l'unica rotta caricata
 * a richiesta: in fondo a questo file (ricaricaPerPezzo), con le stesse
 * guardie, la stessa verifica dei file prima di ricaricare, e un freno suo,
 * legato alla pagina nata dalla ricarica. Prima, quel giorno, Alex l'aveva
 * esclusa.
 */
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { campoScrittoNonUscito } from './uscitaSicura';
import { allaQuiete, richiesteInVolo, scrittureInVolo } from '../services/api';
import { notificaAvviso, useNotifiche } from './notifiche';

/* I manuali lo citano alla lettera. */
export const MSG_VERSIONE_NUOVA =
    'È uscita una versione nuova del gestionale: la pagina si aggiornerà da sola quando avrai finito.';
/* Sulle pagine che lasciano traccia (qui sotto) «quando avrai finito» non
   sarebbe vero: li' si aggiorna solo al cambio di schermata. */
export const MSG_VERSIONE_NUOVA_AL_CAMBIO =
    'È uscita una versione nuova del gestionale: la pagina si aggiornerà quando cambierai schermata.';

/**
 * LE PAGINE CHE LASCIANO TRACCIA ALL'APERTURA (Alex, 2/10 sera, strada C;
 * vedi la testata): qui si ricarica solo al cambio di schermata. Una sola
 * definizione, coi percorsi scritti come nella tabella delle schermate
 * (navigazione/schermate.js). Accanto, le letture con
 * traccia() che ognuna fa all'apertura, MISURATE il 2/10 sera aprendo ogni
 * rotta per ogni ruolo, sul banco della copia e su un database usa e getta:
 * le altre rotte non ne fanno nessuna (l'agenda, la dashboard, il referto
 * compreso). Lo scenario 15 (caso 17) lo rifa' a ogni rilascio: una pagina
 * che ne comincia a fare, o che smette, lo fa fallire finche' l'elenco non
 * e' di nuovo vero. Le letture fatte solo a un gesto (aprire un file, le
 * mancate presentazioni dall'agenda, la ricerca di un paziente) non contano:
 * una ricarica non le rifa'.
 */
export const PAGINE_CON_TRACCIA = [
    '/pazienti',            // elenco_pazienti/ricerca: l'elenco, anche senza una parola cercata
    '/pazienti/:id',        // la cartella: apertura, consensi, schede e avvisi, anamnesi, visite, foto (7 voci; nel profilo 10)
    '/fatturazione',        // fattura/non_fatturate; con ?documento= anche fattura/apertura
    '/registro-accessi',    // registro_accessi/consultazione e /riepilogo
    '/registro-modifiche',  // registro_modifiche/consultazione
];
/* Senza badare alle maiuscole, come il router: App.js non chiede
   caseSensitive, e /Pazienti/12 (un segnalibro, un collegamento scritto a
   mano) apre la cartella. Con un confronto che le distingue quella
   cartella tornava a ricaricarsi da ferma e da nascosta (revisione del
   2/10 sera). Lo controlla il caso 17 dello scenario 15. */
const comeEspressione = (rotta) => new RegExp(`^${rotta.split('/')
    .map((x) => (x.startsWith(':') ? '[^/]+' : x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))).join('/')}/?$`, 'i');
const ESPRESSIONI_TRACCIA = PAGINE_CON_TRACCIA.map(comeEspressione);
/** La pagina (il percorso, senza query) lascia traccia all'apertura? */
export function paginaConTraccia(percorso = window.location.pathname) {
    return ESPRESSIONI_TRACCIA.some((re) => re.test(percorso));
}

/* I tempi, in millisecondi. Le prove dei percorsi (scenario 15) li
   accorciano con window.__rimedichubTempiVersione, messo prima del
   caricamento: aspettare due minuti veri non e' una prova, e' una pausa.
   Mai sotto i 100 ms, cosi' un valore sbagliato non diventa un giro
   continuo di richieste. */
const TEMPI = {
    controllo: 60 * 1000,      // ogni quanto si chiede /index.html (a pagina nascosta: al massimo)
    quiete: 5 * 1000,          // nessun gesto da tanto
    nascosta: 5 * 1000,        // momento (b)
    ferma: 2 * 60 * 1000,      // momento (c)
    freno: 30 * 60 * 1000,     // nessun nuovo tentativo verso la stessa impronta
    avviso: 30 * 60 * 1000,    // dopo quanto la versione che aspetta si dice
    verifica: 15 * 1000,       // al cambio di percorso, quanto vale una HEAD gia' fatta
};

/* La stessa espressione di rilascia.js (bundle()), e quella del css. */
const RE_JS = /main\.[a-f0-9]+\.js/;
const RE_CSS = /main\.[a-f0-9]+\.css/;
const CHIAVE_FRENO = 'rimedichub_versione_ricarica';

let installato = false;
let attivo = false;
let moduliSporchi = () => false;
let propria = null;              // { js, css, impronta }
let nuova = null;                // { js, css, impronta, verificata }
let nuovaDal = null;             // da quando una versione diversa aspetta
let avvisato = false;
let controlloInCorso = false;
let ricaricaPartita = false;
let memoriaUsabile = true;
let natoDaRicarica = false;
let freno = null;                // { verso, fino }
let ultimoGesto = 0;
let ultimoMovimento = 0;
let nascostaDal = null;
let timerNascosta = null;
let permessiTablet = 0;
let schermateTrattenute = 0;     // useTrattieniVersione: segreti a video
let ultimoControllo = 0;         // l'ultimo controllo partito, riuscito o no
const puntatoriPremuti = new Set();
const trattenuti = new Map();    // campo o tela -> la finestra che lo contiene, o null

const adesso = () => Date.now();
const impronta = (js, css) => `${js}|${css || ''}`;
const sulTablet = () => /^\/tablet(\/|$)/.test(window.location.pathname);

function leggiTempi() {
    const prova = window.__rimedichubTempiVersione;
    if (!prova || typeof prova !== 'object') return;
    Object.keys(TEMPI).forEach((k) => {
        const v = Number(prova[k]);
        if (Number.isFinite(v) && v >= 100) TEMPI[k] = v;
    });
}

function versionePropria() {
    const js = [...document.querySelectorAll('script[src]')]
        .map((s) => (s.getAttribute('src') || '').match(/\/static\/js\/(main\.[a-f0-9]+\.js)/))
        .find(Boolean);
    if (!js) return null;
    const css = [...document.querySelectorAll('link[rel="stylesheet"][href]')]
        .map((l) => (l.getAttribute('href') || '').match(/\/static\/css\/(main\.[a-f0-9]+\.css)/))
        .find(Boolean);
    return { js: js[1], css: css ? css[1] : null, impronta: impronta(js[1], css ? css[1] : null) };
}

/* ---------- il freno, in sessionStorage: solo impronta e ora ---------- */
function leggiFreno() {
    try {
        const v = window.sessionStorage.getItem(CHIAVE_FRENO);
        return v ? JSON.parse(v) : null;
    } catch (e) {
        memoriaUsabile = false;
        return null;
    }
}
function scriviFreno(verso) {
    try {
        window.sessionStorage.setItem(CHIAVE_FRENO, JSON.stringify({ verso, il: adesso() }));
    } catch (e) { memoriaUsabile = false; }
}
function togliFreno() {
    try { window.sessionStorage.removeItem(CHIAVE_FRENO); } catch (e) { /* niente da togliere */ }
}

/* ---------- il controllo ---------- */
async function versioneInLinea() {
    let r;
    try {
        r = await fetch('/index.html', { cache: 'no-store', credentials: 'same-origin' });
    } catch (e) { return null; }
    if (r.status !== 200 || !/text\/html/i.test(r.headers.get('content-type') || '')) return null;
    let html;
    try { html = await r.text(); } catch (e) { return null; }
    const js = (html.match(RE_JS) || [])[0];
    if (!js) return null;
    const css = (html.match(RE_CSS) || [])[0] || null;
    return { js, css, impronta: impronta(js, css) };
}

/* Il file c'e' davvero: 200 E il tipo giusto (vedi la testata: try_files). */
async function fileCe(indirizzo, tipo) {
    try {
        const r = await fetch(indirizzo, { method: 'HEAD', cache: 'no-store', credentials: 'same-origin' });
        return r.status === 200 && tipo.test(r.headers.get('content-type') || '');
    } catch (e) { return false; }
}

const fileNuoviCi = async (v) => await fileCe(`/static/js/${v.js}`, /javascript/i)
    && (!v.css || await fileCe(`/static/css/${v.css}`, /text\/css/i));

async function controlla() {
    campiTrattenuti();                                    // la potatura: vedi campiTrattenuti
    if (!attivo || controlloInCorso || ricaricaPartita) return;
    controlloInCorso = true;
    ultimoControllo = adesso();
    try {
        const v = await versioneInLinea();
        if (!v) return;                                   // non riuscito: si riprova al giro dopo
        if (v.impronta === propria.impronta) {            // la stessa (anche dopo un ritorno indietro)
            nuova = null;
            nuovaDal = null;
            return;
        }
        const ci = await fileNuoviCi(v);
        if (!ci) { nuova = null; return; }                // scambio a meta', server giu': niente
        nuova = { ...v, verificata: adesso() };
        if (!nuovaDal) nuovaDal = adesso();
    } finally {
        controlloInCorso = false;
    }
    dopoIlControllo();
}

function dopoIlControllo() {
    if (!nuova || ricaricaPartita) return;
    if (sulTablet()) { tenta('tablet'); return; }      // (d): l'attesa la decide tenta()
    if (document.visibilityState === 'hidden') {
        if (nascostaDal !== null && adesso() - nascostaDal >= TEMPI.nascosta) tenta('nascosta');   // (b)
    } else if (adesso() - ultimoMovimento >= TEMPI.ferma) {
        tenta('ferma');                                   // (c)
    }
    if (!ricaricaPartita) avvisaSeAspettaDaTroppo();
}

function avvisaSeAspettaDaTroppo() {
    /* Solo a pagina visibile: notificaAvviso lo toglie dopo pochi secondi, e
       in una scheda nascosta l'unico avviso promesso non lo vedrebbe nessuno.
       Al ritorno visibile il controllo passa di qui e lo mostra. */
    if (avvisato || sulTablet() || document.visibilityState !== 'visible'
        || !nuovaDal || adesso() - nuovaDal < TEMPI.avviso) return;
    avvisato = true;
    notificaAvviso(paginaConTraccia() ? MSG_VERSIONE_NUOVA_AL_CAMBIO : MSG_VERSIONE_NUOVA);
}

/* ---------- le condizioni ---------- */
/* Toglie dall'elenco i campi e le tele usciti dal documento insieme alla
   loro finestra, e dice quanti ne restano. NON SOLO UNA CONDIZIONE: la
   mappa tiene gli elementi, e con loro i valori scritti e i pixel delle
   firme. Finche' la potava solo percheNo - cioe' solo con una versione
   nuova che aspetta - sul tablet tornato all'attesa restavano vivi, staccati
   dal documento, i campi e le tele di ogni pratica chiusa, fino al rilascio
   dopo (revisione del 2/10, misurato col banco: 2 campi col cognome e il
   codice fiscale e 4 tele firmate per pratica, che si sommavano). Per questo
   si chiama anche a ogni controllo e quando il tablet dichiara l'attesa. */
function campiTrattenuti() {
    trattenuti.forEach((finestra, el) => {
        if (!el.isConnected && !(finestra && finestra.isConnected)) trattenuti.delete(el);
    });
    return trattenuti.size;
}

/**
 * Perche' adesso no (una parola), o null se si puo'. `richiestePrima`: al
 * cambio di percorso le richieste in volo si contano com'erano PRIMA che la
 * pagina di destinazione cominciasse a caricare i suoi dati (quelle la
 * ricarica le rifa'), piu' le scritture partite dopo.
 */
function percheNo(momento, richiestePrima = null) {
    if (!attivo || ricaricaPartita || !nuova) return 'niente';
    if (freno && freno.verso === nuova.impronta && adesso() < freno.fino) return 'freno';
    if (maiPiu()) return 'freno';
    return ostacolo(momento, richiestePrima);
}

/* Senza sessionStorage il freno non si scrive: una pagina nata da una
   ricarica non ne fa un'altra da sola (vedi IL FRENO). */
const maiPiu = () => !memoriaUsabile && natoDaRicarica;

/* La pagina e' nata da una ricarica (anche quella di F5)? La chiedono il
   freno della versione e quello del pezzo a richiesta (ricaricaPerPezzo). */
function nataDaRicarica() {
    try {
        const n = performance.getEntriesByType('navigation')[0];
        return !!n && n.type === 'reload';
    } catch (e) { return false; }
}

/**
 * Le guardie che non dipendono dalla versione nuova: c'e' qualcosa a meta'
 * (una parola), o null. A pagina nascosta si chiede questo PRIMA di andare
 * in rete, anche quando nessuna versione nuova e' ancora stata vista: una
 * scheda in secondo piano con un modulo sporco non chiede /index.html, e lo
 * chiede al giro dopo quello in cui il modulo e' tornato pulito.
 */
function ostacolo(momento, richiestePrima = null) {
    /* Senza rete la ricarica porterebbe alla pagina d'errore del browser, al
       posto di un'applicazione che sarebbe rimasta in piedi. Al cambio di
       percorso lo si guarda gia' prima della HEAD; qui vale per tutti i
       momenti, e conta di piu' a pagina nascosta, dove nessuno vede la
       ricarica partire e la pagina d'errore resterebbe li' fino al ritorno. */
    if (navigator.onLine === false) return 'rete';
    if (sulTablet() && permessiTablet === 0) return 'tablet';
    /* Strada C (Alex, 2/10 sera): sulle pagine che lasciano traccia
       all'apertura si ricarica solo al cambio di schermata fatto dalla
       persona. Vale da ferma e da nascosta, e da nascosta vuol dire anche
       niente rete: passoNascosta chiede questo prima di andarci. */
    if (momento !== 'navigazione' && paginaConTraccia()) return 'traccia';
    if (schermateTrattenute > 0) return 'segreto';
    if (moduliSporchi()) return 'modulo';
    if (campoScrittoNonUscito()) return 'campo';
    if (richiestePrima !== null) {
        if (richiestePrima > 0 || scrittureInVolo() > 0) return 'richiesta';
    } else if (richiesteInVolo() > 0) {
        return 'richiesta';
    }
    if (campiTrattenuti() > 0) return 'trattenuto';
    /* Un errore a video resta finche' qualcuno non lo chiude (notifiche.js):
       la ricarica lo cancellerebbe, anche al cambio di percorso, dove senza
       ricarica sarebbe rimasto. Dopo 30 minuti l'avviso dice che si aspetta. */
    if (useNotifiche.getState().voci.some((v) => v.tipo === 'errore')) return 'errore';
    /* Al cambio di percorso il gesto e' il clic che ha cambiato pagina: la
       pagina di prima non c'e' piu'. Negli altri momenti, chi sta toccando
       o ha appena toccato non si interrompe. */
    if (momento !== 'navigazione'
        && (puntatoriPremuti.size > 0 || adesso() - ultimoGesto < TEMPI.quiete)) return 'gesto';
    return null;
}

function tenta(momento, richiestePrima = null) {
    const no = percheNo(momento, richiestePrima);
    if (no) {
        /* Ferma SOLO per delle letture in volo: si riprova quando tornano,
           non al prossimo timer (vedi alRitornoDelleLetture). Non al cambio
           di percorso, che decide su quello che c'era al clic. */
        if (no === 'richiesta' && momento !== 'navigazione' && scrittureInVolo() === 0) allaQuiete(alRitornoDelleLetture);
        return false;
    }
    scriviFreno(nuova.impronta);
    /* Senza memoria il freno non si puo' scrivere: una pagina nata da una
       ricarica non ne fa un'altra da sola. */
    if (maiPiu()) return false;
    ricaricaPartita = true;
    window.location.reload();
    return true;
}

/* ---------- gli ascoltatori ---------- */
/* Si ricorda anche la finestra che contiene il campo: in una finestra a passi
   il passo di prima esce dal documento, ma i suoi valori restano nello stato
   del componente fino alla chiusura (revisione del 2/10: CompletamentoConsensi,
   la firma del passo 1 persa a una ricarica al passo 2). */
function ricorda(el) {
    if (trattenuti.has(el)) return;
    trattenuti.set(el, el.closest('[role="dialog"], [aria-modal="true"]'));
}

function ricordaCampo(e) {
    const el = e.target;
    if (!el || !el.closest) return;
    if (el.closest('[data-salva-da-solo]') || el.closest('[data-non-trattiene]')) return;
    if (el.tagName === 'INPUT' && String(el.type).toLowerCase() === 'search') return;
    ricorda(el);
}

/* Ogni quanto, al massimo, una pagina nascosta va in rete: un giro (60 s),
   meno un passo di questo timer (5 s). La tolleranza viene dalla misura (vedi
   I TIMER A PAGINA NASCOSTA in testa): dopo 5 minuti in secondo piano Chromium
   sveglia i timer una volta al minuto, allineati: con «almeno 60 s»
   basterebbe un millisecondo di scarto fra due risvegli per saltarne uno, e
   la scheda andrebbe in rete ogni due minuti invece che ogni minuto. */
const giroNascosta = () => TEMPI.controllo - TEMPI.nascosta;

/* La verifica dei file nuovi vale per ricaricare se l'ha fatta l'ultimo
   controllo (uno andato male dopo non la lascia valere) e se ha meno di
   TEMPI.verifica (15 s): la rete o il server possono essere caduti nel
   frattempo. */
const verificaFresca = () => !!nuova && nuova.verificata >= ultimoControllo
    && adesso() - nuova.verificata <= TEMPI.verifica;

/**
 * Le letture in volo sono tornate (services/api.js, allaQuiete). LA
 * REVISIONE DEL 2/10, MISURATA con Chromium 131 su /dashboard come
 * amministrazione: in una scheda nascosta da piu' di 5 minuti i timer si
 * svegliano una volta al minuto TUTTI INSIEME, e con il controllo della
 * versione partivano le letture periodiche degli avvisi del Layout
 * (/api/alert/due, /api/agenda/ritardi: 60 s). La HEAD sui file nuovi
 * finiva in 11-26 ms, le letture in 20-34: la ricarica si fermava su
 * «richiesta» sette controlli su sette, e il controllo dopo era di nuovo
 * insieme alle letture. Lo stesso a pagina visibile e ferma: i due
 * setInterval da 60 s nascono nello stesso giro di React. Una scheda
 * nascosta da tempo non si aggiornava MAI; prima della scheda gia' nascosta
 * neanche quella ferma da due minuti.
 * Adesso si riprova appena tornano, con tutte le guardie da capo e solo su
 * una verifica ancora fresca; se nel frattempo e' partita una scrittura, o
 * il modulo e' sporco, niente: decide il giro dopo, come sempre. A pagina
 * nascosta si riprova il passo intero (passoNascosta): se il giro era
 * rimasto fermo PRIMA di andare in rete, il controllo parte adesso.
 */
function alRitornoDelleLetture() {
    if (!attivo || ricaricaPartita) return;
    if (document.visibilityState === 'hidden') passoNascosta();
    else if (verificaFresca()) dopoIlControllo();
}

function quandoNascosta() {
    timerNascosta = null;
    if (document.visibilityState !== 'hidden' || ricaricaPartita) return;
    passoNascosta();
    timerNascosta = setTimeout(quandoNascosta, TEMPI.nascosta);
}

function passoNascosta() {
    /* A pagina nascosta il controllo lo fa questo giro (il setInterval
       guarda solo le pagine visibili), ANCHE SE LA VERSIONE NUOVA NON E'
       ANCORA STATA VISTA: una scheda gia' in secondo piano al momento del
       rilascio si aggiorna da nascosta (Alex, 2/10 pomeriggio: «si ricarica
       anche da nascosta, mai con un modulo sporco»). Fino a quel giorno
       restava sul codice di prima finche' qualcuno non la riguardava.
       In rete si va solo se si potrebbe ricaricare davvero (ostacolo: tutte
       le guardie tranne la versione) e al massimo una volta al giro (60 s):
       prima della revisione del 2/10 ogni 5 secondi partivano un /index.html
       e due HEAD, per ore, da ogni scheda in secondo piano con un modulo a
       meta'. Appena il modulo e' pulito, il controllo parte al giro dopo.
       La ricarica parte solo su una verifica dei file nuovi fatta
       dall'ultimo controllo (uno andato male non la autorizza) e di meno di
       15 secondi (TEMPI.verifica, come al cambio di percorso): la rete o il
       server possono essere caduti nel frattempo, e una scheda ricaricata
       senza rete resterebbe sulla pagina d'errore del browser fino al
       ritorno di qualcuno. Con una verifica vecchia si aspetta il giro.
       Se a fermarlo sono soltanto delle letture in volo (gli avvisi del
       Layout, svegliati insieme a questo timer), il passo si rifa' quando
       tornano: vedi alRitornoDelleLetture. */
    if (!attivo || ricaricaPartita || maiPiu()) return;
    const no = ostacolo('nascosta');
    if (no === 'richiesta' && scrittureInVolo() === 0) { allaQuiete(alRitornoDelleLetture); return; }
    if (no) return;
    if (verificaFresca()) dopoIlControllo();
    else if (adesso() - ultimoControllo >= giroNascosta()) controlla();
}

function installa() {
    if (installato || typeof window === 'undefined' || typeof document === 'undefined') return;
    installato = true;
    propria = versionePropria();
    if (!propria || typeof fetch !== 'function') return;
    leggiTempi();
    attivo = true;
    ultimoGesto = adesso() - TEMPI.quiete;
    ultimoMovimento = adesso();

    const f = leggiFreno();
    if (f && f.verso) {
        if (f.verso === propria.impronta) togliFreno();                       // arrivati
        else if (adesso() - Number(f.il || 0) < TEMPI.freno) freno = { verso: f.verso, fino: Number(f.il) + TEMPI.freno };
        else togliFreno();
    }
    natoDaRicarica = nataDaRicarica();

    const gesto = () => { ultimoGesto = adesso(); ultimoMovimento = ultimoGesto; };
    const movimento = () => { ultimoMovimento = adesso(); };
    const opz = { capture: true, passive: true };
    document.addEventListener('pointerdown', (e) => {
        gesto();
        puntatoriPremuti.add(e.pointerId);
        if (e.target && e.target.tagName === 'CANVAS' && e.target.closest) ricorda(e.target);
    }, opz);
    const alzato = (e) => { puntatoriPremuti.delete(e.pointerId); gesto(); };
    document.addEventListener('pointerup', alzato, opz);
    document.addEventListener('pointercancel', alzato, opz);
    /* Una finestra che perde il fuoco non riceve piu' i rilasci: un dito
       «premuto per sempre» fermerebbe l'aggiornamento per sempre. */
    window.addEventListener('blur', () => puntatoriPremuti.clear());
    document.addEventListener('keydown', gesto, opz);
    document.addEventListener('input', (e) => { gesto(); ricordaCampo(e); }, opz);
    document.addEventListener('change', ricordaCampo, opz);
    ['scroll', 'wheel', 'pointermove', 'touchmove'].forEach((t) => document.addEventListener(t, movimento, opz));

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            nascostaDal = adesso();
            puntatoriPremuti.clear();
            if (!timerNascosta) timerNascosta = setTimeout(quandoNascosta, TEMPI.nascosta);
        } else {
            nascostaDal = null;
            if (timerNascosta) { clearTimeout(timerNascosta); timerNascosta = null; }
            movimento();
            controlla();
        }
    });
    window.addEventListener('online', () => controlla());
    /* A pagina nascosta il giro e' quandoNascosta: vedi li'. */
    setInterval(() => { if (document.visibilityState === 'visible') controlla(); }, TEMPI.controllo);
    /* Una pagina che NASCE nascosta - aperta in una scheda in secondo piano,
       o ricaricata da nascosta proprio da qui - non riceve visibilitychange
       finche' qualcuno non la guarda: il suo giro parte subito, altrimenti
       una scheda aggiornata da nascosta non si aggiornerebbe piu' fino al
       ritorno visibile, e a un secondo rilascio resterebbe indietro. */
    if (document.visibilityState === 'hidden') {
        nascostaDal = adesso();
        timerNascosta = setTimeout(quandoNascosta, TEMPI.nascosta);
    }
}

/**
 * Montato UNA volta, in GuardiaUscita (utils/uscitaProtetta.js), accanto a
 * installaUscitaSicura. `sporchi`: chi dice se c'e' un modulo sporco.
 */
export function useVersioneInLinea(sporchi) {
    const { pathname, state } = useLocation();
    const primo = useRef(true);
    useEffect(() => {
        if (typeof sporchi === 'function') moduliSporchi = sporchi;
        installa();
    }, [sporchi]);
    /* (a) Il cambio di percorso. Nel layout effect, cioe' prima che la pagina
       di destinazione cominci a chiedere i suoi dati: le richieste in volo
       contate qui sono quelle della pagina di prima. La decisione si prende
       un attimo dopo, quando anche le pulizie della pagina di prima sono
       partite (una scrittura all'uscita ferma la ricarica). */
    useLayoutEffect(() => {
        if (primo.current) { primo.current = false; return undefined; }
        if (!attivo || !nuova) return undefined;
        /* Un cambio di schermata fatto per un'AZIONE - «Nuovo compito» da
           Ctrl K o dal pulsante «Nuovo» del guscio (stile «Console»,
           navigazione/useAzione.js) - non ricarica. L'azione viaggia nello
           stato della posizione, e la pagina di destinazione la consuma
           subito, sostituendo la voce della cronologia: una ricarica un
           attimo dopo ritroverebbe la pagina senza azione, e il modulo chiesto
           non si aprirebbe (o si aprirebbe e sparirebbe). Trovato all'unione
           dei rami console e ricarica (3/10/2026). La versione nuova arriva al
           cambio di schermata dopo, o nei momenti di sempre. */
        if (state && state.azione) return undefined;
        /* Senza rete la ricarica porterebbe alla pagina d'errore del browser,
           al posto di un'applicazione che sarebbe rimasta in piedi. */
        if (navigator.onLine === false) return undefined;
        const prima = richiesteInVolo();
        if (adesso() - nuova.verificata <= TEMPI.verifica) {
            const t = setTimeout(() => tenta('navigazione', prima), 0);
            return () => clearTimeout(t);
        }
        /* Una verifica vecchia (fino a un giro, 60 s) non basta: i file in
           linea possono essere cambiati di nuovo, il server o la rete caduti.
           Si rifa' la HEAD, e si decide solo se risponde 200 col tipo giusto. */
        const v = nuova;
        let annullata = false;
        fileNuoviCi(v).then((ci) => {
            if (annullata || !ci || nuova !== v) return;
            v.verificata = adesso();
            tenta('navigazione', prima);
        });
        return () => { annullata = true; };
    }, [pathname]);
}

/**
 * Il tablet (pages/Tablet.jsx) dichiara quando e' in attesa: solo allora,
 * e al controllo successivo, si puo' ricaricare. Il predefinito e' no.
 */
export function useRicaricaPermessa(permessa) {
    useEffect(() => {
        if (!permessa) return undefined;
        /* Tornati all'attesa, il modulo della pratica appena chiusa e' gia'
           smontato: i suoi campi e le sue tele escono subito dall'elenco,
           e con loro i dati del paziente di prima (vedi campiTrattenuti). */
        campiTrattenuti();
        permessiTablet += 1;
        return () => { permessiTablet -= 1; };
    }, [permessa]);
}

/**
 * Una schermata che mostra un segreto UNA volta sola lo dichiara finche' e'
 * a video: la chiave di un tablet (pages/Dispositivi.jsx), i codici di
 * recupero e il QR dell'attivazione (pages/Profilo.jsx), il PIN generato
 * (pages/AreaDipendenti.jsx). Trovato alla revisione del 2/10: si rigenera
 * la chiave, si va al tablet a scriverla, il PC resta fermo due minuti, la
 * pagina si ricarica e la chiave sparisce - con quella vecchia gia'
 * bruciata. Un contatore, come quello del tablet; vale in ogni momento,
 * anche al cambio di percorso.
 */
export function useTrattieniVersione(trattieni) {
    useEffect(() => {
        if (!trattieni) return undefined;
        schermateTrattenute += 1;
        return () => { schermateTrattenute -= 1; };
    }, [trattieni]);
}

/* ---------- la «seconda difesa»: il pezzo a richiesta che non arriva ---------- */
/**
 * LA SECONDA DIFESA (Alex, 2/10 notte; prima l'aveva esclusa). Il
 * Referto si carica a richiesta (React.lazy in navigazione/schermate.js): il
 * suo codice e' un file a parte, static/js/<n>.<impronta>.chunk.js col suo .css, chiesto la
 * prima volta che la pagina apre un referto. Nei secondi dello scambio della
 * build quel file, per una pagina aperta col codice di prima, puo' mancare:
 * rilascia.js rinomina le cartelle e SOLO DOPO ricopia accanto ai nuovi i
 * file della versione precedente (cp -rn); in mezzo Nginx risponde 404, o
 * con try_files 200 con l'HTML dell'applicazione, che il browser rifiuta di
 * eseguire (nosniff). webpack solleva ChunkLoadError, e fino a quel giorno al
 * posto del referto c'era la pagina d'errore del router («Unexpected
 * Application Error!»), da cui si usciva solo ricaricando a mano.
 *
 * ADESSO la rotta ha una pagina d'errore sua (components/PezzoNonCaricato,
 * errorElement in App.js), che chiede qui: la pagina si ricarica da sola UNA
 * volta - quasi sempre basta: torna la pagina della versione in linea, e il
 * suo pezzo c'e' - e se non basta lo dice, con «Ricarica».
 *
 * LE GUARDIE SONO QUELLE DI SEMPRE, chieste a ostacolo() e non copiate, nel
 * momento della navigazione: la persona ha appena chiesto il referto, quindi
 * il clic non conta come gesto e le letture in volo non contano (la ricarica
 * le rifa'); le scritture si'. Con una scrittura in volo PezzoNonCaricato
 * aspetta che torni (allaQuiete) e chiede di nuovo; con un modulo sporco, un
 * campo scritto e non uscito, un campo trattenuto, un segreto o un errore a
 * video, o senza rete, niente ricarica: il messaggio.
 *
 * E PRIMA DI RICARICARE I FILE CI DEVONO ESSERE, come per la terza difesa:
 * versioneInLinea() e fileNuoviCi(), le stesse funzioni di qui sopra -
 * /index.html senza cache, e la HEAD sui suoi main.<impronta>.js e .css, 200
 * col tipo giusto. La guardia della rete legge navigator.onLine, che dice
 * solo se il computer e' collegato: col Wi-Fi su e il server o internet giu'
 * - la «rete caduta» piu' comune - e' vero. Misurato alla revisione del
 * 3/10, senza questa verifica: una ricarica, e la pagina restava su quella
 * d'errore del browser anche dopo il ritorno del server, senza mai il
 * messaggio. Dopo la risposta le guardie si chiedono da capo: nel frattempo
 * puo' essere partita una scrittura, o la persona essere andata altrove
 * (`ancora`, da chi chiama).
 *
 * IL FRENO, suo e legato alla pagina: in sessionStorage solo l'ora
 * dell'ultima ricarica fatta per un pezzo (anche da «Ricarica»). Vale per la
 * pagina NATA DA QUELLA RICARICA - navigazione di tipo reload, cominciata
 * entro FINESTRA_NASCITA da quell'ora -: li' non si ricarica di nuovo da
 * soli, si mostra il messaggio. Non si toglie quando il pezzo arriva: fino
 * alla revisione del 3/10 si toglieva, e con un pezzo annidato che manca -
 * il pezzo esterno arriva, quello dentro no - la pagina rifaceva il giro
 * (misurato dal revisore su una variante con l'editor a parte: 26 documenti
 * in 6 s). Legato alla nascita della pagina, nessuna combinazione di pezzi
 * lo rifa'; e una pagina aperta dopo (un indirizzo nuovo, un F5 fatto piu'
 * tardi) ha di nuovo la sua ricarica, anche col freno ancora scritto.
 * IL FRENO CONTA SOLO SE SI RILEGGE: una memoria che legge ma non scrive
 * (setItem rifiutato, QuotaExceededError) diceva «nessun freno» a ogni
 * pagina, e il giro tornava (31 documenti in 6 s, revisione del 3/10).
 * Senza un freno riletto - memoria negata o piena - una pagina nata da una
 * ricarica non ne fa un'altra da sola, come per la versione.
 *
 * SOLO SUL REFERTO, perche' e' l'unica rotta caricata a richiesta: il resto
 * sta nel bundle principale, che o arriva tutto o non parte niente. Una rotta
 * nuova caricata a richiesta prende lo stesso errorElement.
 */
const CHIAVE_PEZZO = 'rimedichub_pezzo_ricarica';
/* Quanto dopo l'ora scritta nel freno puo' cominciare la pagina della
   ricarica. La navigazione parte subito dopo reload(): nessun «vuoi lasciare
   la pagina?» la trattiene, perche' le guardie l'hanno esclusa. Largo di
   proposito: un F5 della persona dentro il minuto trova il messaggio, che e'
   il male minore. */
const FINESTRA_NASCITA = 60 * 1000;

/** L'errore e' un pezzo di codice (js o css) che non e' arrivato? Il nome e'
 *  quello che danno webpack e il caricatore dei css (CRA 5). */
export function pezzoNonCaricato(errore) {
    return !!errore && (errore.name === 'ChunkLoadError' || errore.code === 'CSS_CHUNK_LOAD_FAILED');
}

/* Quando e' cominciata questa pagina, in millisecondi come Date.now(); null
   se il browser non lo dice. */
function nascitaPagina() {
    try {
        if (performance.timeOrigin) return performance.timeOrigin;
        return (performance.timing && performance.timing.navigationStart) || null;
    } catch (e) { return null; }
}

/* true: questa pagina e' nata dalla ricarica scritta nel freno; false: no;
   null: sessionStorage non si legge. */
function frenoPezzo() {
    let f;
    try {
        const v = window.sessionStorage.getItem(CHIAVE_PEZZO);
        f = v ? JSON.parse(v) : null;
    } catch (e) { return null; }
    if (!f || !nataDaRicarica()) return false;
    const nata = nascitaPagina();
    /* Senza l'ora di nascita non si sa quale ricarica sia: nata da una
       ricarica, col freno scritto, non ne fa un'altra. */
    return nata === null || Math.abs(nata - Number(f.il || 0)) < FINESTRA_NASCITA;
}

/* Scrive il freno e dice se c'e' davvero: true solo se, riletto, e' quello
   appena scritto (vedi IL FRENO CONTA SOLO SE SI RILEGGE). */
function scriviFrenoPezzo() {
    const v = JSON.stringify({ il: adesso() });
    try {
        window.sessionStorage.setItem(CHIAVE_PEZZO, v);
        return window.sessionStorage.getItem(CHIAVE_PEZZO) === v;
    } catch (e) { return false; }
}

/* Perche' adesso no, prima di andare in rete e dopo: il freno, poi le
   guardie di sempre (ostacolo). */
function percheNoPezzo() {
    const f = frenoPezzo();
    if (f === true || (f === null && nataDaRicarica())) return 'freno';
    return ostacolo('navigazione', 0);
}

/**
 * Il pezzo non e' arrivato: ricarica la pagina, se si puo'. Torna (una
 * promessa di) null se la ricarica e' partita, o era gia' partita;
 * altrimenti perche' no, in una parola: 'freno'; 'server', i file in linea
 * non rispondono come devono; 'via', chi ha chiesto non c'e' piu'
 * (`ancora` ha detto no dopo la rete); o quella di ostacolo(). 'richiesta'
 * e' una scrittura in volo: chi chiama aspetta che torni e chiede di nuovo.
 */
export async function ricaricaPerPezzo(ancora = () => true) {
    if (ricaricaPartita) return null;
    const prima = percheNoPezzo();
    if (prima) return prima;
    const v = await versioneInLinea();
    const ci = !!v && await fileNuoviCi(v);
    if (ricaricaPartita) return null;
    if (!ancora()) return 'via';
    if (!ci) return 'server';
    const dopo = percheNoPezzo();
    if (dopo) return dopo;
    /* Un freno che non si rilegge non ferma la pagina dopo: se questa e'
       gia' nata da una ricarica, niente. */
    if (!scriviFrenoPezzo() && nataDaRicarica()) return 'freno';
    ricaricaPartita = true;
    window.location.reload();
    return null;
}

/** «Ricarica» nel messaggio: la sceglie la persona, come F5, e conta per il
 *  freno: se il pezzo non arriva nemmeno dopo, il messaggio torna subito,
 *  senza un'altra ricarica da sola (e se il freno non si scrive, la pagina
 *  nata dalla ricarica non ne fa un'altra lo stesso). */
export function ricaricaAMano() {
    scriviFrenoPezzo();
    window.location.reload();
}
